import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import Header from '../components/organisms/Header';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { API_BASE_URL } from '../config';

// Build headers with JWT Authorization from SessionManager
const buildAuthHeaders = (extra = {}) => {
  let session = SessionManager.getCurrentSession();
  if (!session || !session.access_token) {
    session = SessionManager.getSession('candidate') || SessionManager.getSession('hr');
  }
  const token = session?.access_token;
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const AssessmentWelcomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [candidateName, setCandidateName] = useState('');
  const [loading, setLoading] = useState(true);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [jobTitle, setJobTitle] = useState('');

  useEffect(() => {
    const checkApplicationStatus = async () => {
      const candidateId = searchParams.get('candidate_id') || location.state?.candidateId;
      const jobId = searchParams.get('job_id') || location.state?.jobId;
      const fromInvite = searchParams.get('candidate_id');

      const session = SessionManager.getSession('candidate');
      const name = location.state?.candidateName ||
                   session?.profile?.full_name ||
                   `${session?.profile?.first_name || ''} ${session?.profile?.last_name || ''}`.trim() ||
                   localStorage.getItem('dutlaunch_candidate_name') ||
                   'Candidate';
      setCandidateName(name);

      if (fromInvite) {
        setLoading(false);
        return;
      }

      if (!jobId || !candidateId) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/candidates/${candidateId}/applications`, { headers: buildAuthHeaders() });
        if (!response.ok) throw new Error('Failed to fetch applications');

        const data = await response.json();
        const existingApplication = data.applications?.find(app => app.job_id === jobId);

        if (existingApplication) {
          setApplicationStatus(existingApplication);
          setJobTitle(existingApplication.job_title);

          // Fetch job's ATS pass threshold (HR-configurable, default 50)
          let atsThreshold = 50;
          try {
            const jobRes = await fetch(`${API_BASE_URL}/jobs/${jobId}`);
            if (jobRes.ok) {
              const jobData = await jobRes.json();
              atsThreshold = jobData.ats_pass_threshold || 50;
            }
          } catch { /* use default */ }

          if (existingApplication.status === 'interview_completed' ||
              existingApplication.status === 'completed' ||
              existingApplication.interview_taken) {
            setApplicationStatus({ ...existingApplication, status: 'interview_taken' });
          }
          else if (existingApplication.interview_details?.interview_id &&
                   existingApplication.status !== 'interview_completed') {
            setApplicationStatus({ ...existingApplication, status: 'interview_in_progress' });
          }
          else if (existingApplication.ats_score !== null && existingApplication.ats_score < atsThreshold) {
            setApplicationStatus({ ...existingApplication, status: 'ats_failed', ats_threshold: atsThreshold });
          }
          else if (existingApplication.ats_score !== null && existingApplication.ats_score >= atsThreshold) {
            setApplicationStatus({ ...existingApplication, status: 'ats_passed' });
          }
        }
      } catch (error) {
        console.error('Error checking application status:', error);
      } finally {
        setLoading(false);
      }
    };

    checkApplicationStatus();
  }, [location.state, searchParams]);

  const handleContinue = () => {
    if (applicationStatus?.status === 'interview_in_progress') {
      navigate('/assessment/device-check', {
        state: {
          applicationId: applicationStatus.id,
          jobTitle: applicationStatus.job_title,
          candidateName: candidateName,
          skipResumeMatch: true,
          continueInterview: true,
          interviewId: applicationStatus.interview_details?.interview_id
        }
      });
    }
    else if (applicationStatus?.status === 'ats_passed') {
      navigate('/assessment/device-check', {
        state: {
          applicationId: applicationStatus.id,
          jobTitle: applicationStatus.job_title,
          candidateName: candidateName,
          skipResumeMatch: true
        }
      });
    } else {
      navigate('/assessment/device-check', {
        state: {
          applicationId: location.state?.applicationId,
          jobTitle: location.state?.jobTitle || jobTitle,
          candidateName: candidateName
        }
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header variant="default" />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-10 h-10 border-2 border-[#0a1929] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500 text-sm">Loading...</p>
          </div>
        </main>
      </div>
    );
  }

  // Interview Already Taken
  if (applicationStatus?.status === 'interview_taken') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header variant="default" />
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-lg">
            <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center">
              <div className="w-14 h-14 bg-[#0a1929] rounded-xl flex items-center justify-center mx-auto mb-6">
                <Icon name="check" size={28} className="text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-3">Interview Completed</h1>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                You have already completed the interview for <span className="font-medium text-gray-900">{jobTitle}</span>. Your application is under review.
              </p>
              {applicationStatus.ats_score && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mb-6 inline-block">
                  <span className="text-xs text-gray-500 uppercase tracking-wide">ATS Score</span>
                  <p className="text-xl font-bold text-[#0a1929]">{applicationStatus.ats_score.toFixed(1)}%</p>
                </div>
              )}
              <button
                onClick={() => navigate('/jobs')}
                className="w-full py-3 bg-[#0a1929] hover:bg-[#0d2137] text-white text-sm font-semibold rounded-xl transition-colors"
              >
                Explore Other Jobs
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ATS Failed
  if (applicationStatus?.status === 'ats_failed') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header variant="default" />
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-lg">
            <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center">
              <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-6">
                <Icon name="close" size={28} className="text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-3">Application Not Matched</h1>
              <p className="text-gray-500 text-sm leading-relaxed mb-2">
                Thank you for your interest in <span className="font-medium text-gray-900">{jobTitle}</span>.
              </p>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                Your resume did not meet the minimum requirements for this role.
              </p>
              <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-6">
                <span className="text-xs text-gray-500 uppercase tracking-wide">ATS Score</span>
                <p className="text-xl font-bold text-red-600">{applicationStatus.ats_score?.toFixed(1)}%</p>
              </div>
              <button
                onClick={() => navigate('/jobs')}
                className="w-full py-3 bg-[#0a1929] hover:bg-[#0d2137] text-white text-sm font-semibold rounded-xl transition-colors"
              >
                Explore Other Jobs
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ATS Passed or Interview In Progress
  if (applicationStatus?.status === 'ats_passed' || applicationStatus?.status === 'interview_in_progress') {
    const isInProgress = applicationStatus?.status === 'interview_in_progress';

    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header variant="default" />
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-2xl">
            <div className="bg-white border border-gray-200 rounded-2xl p-10">
              {/* Status badge */}
              <div className="flex items-center gap-2 mb-8">
                <div className={`w-2 h-2 rounded-full ${isInProgress ? 'bg-blue-500 animate-pulse' : 'bg-emerald-500'}`}></div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {isInProgress ? 'Interview In Progress' : 'Resume Matched'}
                </span>
              </div>

              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {isInProgress ? 'Continue Your Interview' : 'Welcome Back'}
              </h1>
              <p className="text-gray-500 text-sm mb-6">Hi {candidateName},</p>

              {/* Info card */}
              <div className={`rounded-xl p-4 mb-8 ${isInProgress ? 'bg-blue-50 border border-blue-100' : 'bg-emerald-50 border border-emerald-100'}`}>
                {isInProgress ? (
                  <div>
                    <p className="text-sm font-medium text-gray-900">You have an interview in progress for {jobTitle}</p>
                    <p className="text-xs text-gray-500 mt-1">Click below to continue from where you left off</p>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Resume matched for {jobTitle}</p>
                      <p className="text-xs text-gray-500 mt-1">You can proceed directly to the interview</p>
                    </div>
                    <div className="text-right">
                      <span className="text-xs text-gray-500">ATS Score</span>
                      <p className="text-lg font-bold text-[#0a1929]">{applicationStatus.ats_score?.toFixed(1)}%</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Checklist */}
              <div className="mb-8">
                <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-4">Before you begin</h3>
                <div className="space-y-3">
                  {[
                    { icon: 'mic', text: 'Respond verbally to interview questions' },
                    { icon: 'volume_off', text: 'Quiet environment with minimal background noise' },
                    { icon: 'wifi', text: 'Stable internet connection' },
                    { icon: 'videocam', text: 'Session will be recorded for assessment' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name={item.icon} size={16} className="text-[#0a1929]" />
                      </div>
                      <span className="text-sm text-gray-600">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={handleContinue}
                className="w-full py-3.5 bg-[#0a1929] hover:bg-[#0d2137] text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                {isInProgress ? 'Continue Interview' : 'Proceed to Setup'}
                <Icon name="arrow_forward" size={18} className="text-white" />
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Default Welcome Page
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header variant="default" />

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-2xl">
          <div className="bg-white border border-gray-200 rounded-2xl p-10">
            {/* Header section */}
            <div className="mb-8">
              <div className="w-12 h-12 bg-[#0a1929] rounded-xl flex items-center justify-center mb-6">
                <Icon name="record_voice_over" size={24} className="text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome</h1>
              <p className="text-gray-500 text-sm">Hi {candidateName},</p>
            </div>

            <p className="text-gray-600 text-sm leading-relaxed mb-8">
              Thank you for taking the time to attend this interview. This will be a virtual, AI-conducted
              interview and is expected to take approximately 15 minutes to complete.
            </p>

            {/* Checklist */}
            <div className="mb-8">
              <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-4">Important points</h3>
              <div className="space-y-3">
                {[
                  { icon: 'mic', text: 'You will respond verbally to a series of questions' },
                  { icon: 'volume_off', text: 'Ensure a quiet environment with minimal background noise' },
                  { icon: 'wifi', text: 'Stable internet connection to avoid interruptions' },
                  { icon: 'videocam', text: 'Session will be recorded for assessment purposes' },
                  { icon: 'psychology', text: 'Assesses communication, problem-solving, and professional approach' },
                  { icon: 'schedule', text: 'Approximately 15 minutes to complete' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={item.icon} size={16} className="text-[#0a1929]" />
                    </div>
                    <span className="text-sm text-gray-600">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-gray-400 text-xs mb-6">
              When you're ready, click Continue to set up your camera and microphone.
            </p>

            <button
              onClick={handleContinue}
              className="w-full py-3.5 bg-[#0a1929] hover:bg-[#0d2137] text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              Continue
              <Icon name="arrow_forward" size={18} className="text-white" />
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AssessmentWelcomePage;
